/**
 * Sanitize a URL intended for use in an <img src> or similar attribute.
 * Blocks javascript:, data: (except safe image data URIs), and other
 * dangerous protocols. Returns empty string for invalid URLs.
 */
export function sanitizeImageUrl(url: string | null | undefined): string {
  if (!url) return "";
  const trimmed = url.trim();
  if (!trimmed) return "";

  // Block dangerous protocols
  if (/^\s*(javascript|vbscript|data(?!:image\/(png|jpe?g|gif|svg\+xml|webp);base64,))\s*:/i.test(trimmed)) {
    return "";
  }

  // Must be http(s) or a relative path or a safe data: URI
  if (/^https?:\/\//i.test(trimmed) || /^\/[^/]/.test(trimmed) || /^data:image\//i.test(trimmed)) {
    return trimmed;
  }

  // Reject anything else (e.g. ftp:, file:, custom protocols)
  return "";
}

export function escapeHtml(unsafe: string | number | null | undefined): string {
  if (unsafe === null || unsafe === undefined) {
    return "";
  }
  return String(unsafe)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

/**
 * Normalize a value that will be sent to an endpoint doing its own
 * context-specific escaping server-side (e.g. interpolate()'s html/text/
 * subject contexts in supabase/functions/_shared/email-template-interpolate.ts)
 * -- strips null bytes and trims/caps length, but does NOT strip tags or
 * HTML-escape.
 * sanitizeTextInput's escapeHtml() is right for values rendered straight into
 * this app's own DOM, but wrong for a value the server will escape again:
 * e.g. "O'Connor" -> "O&#039;Connor" pre-escaped here, then re-escaped
 * server-side into "O&amp;#039;Connor", displaying the literal entity text
 * to the recipient (#4634 round-2/round-7 findings on PR #4990).
 * Codex round-8 finding: the tag-stripping regex (`<[^>]*>`) previously here
 * also matched ordinary comparison text like "2 < 3 > 1" as a fake tag and
 * silently deleted it. The server's own interpolate() context-specific
 * escaping (HTML-escaped for the html context, raw for text/subject) is the
 * actual XSS defense layer here -- this helper's job is only "don't
 * pre-escape", not "strip markup" -- so dropped the tag-stripping entirely
 * rather than trying to special-case comparison operators.
 */
export const normalizeForServerEscaping = (value: string | number | null | undefined, maxLength = 1000): string => {
  if (!value) return "";
  return String(value)
    .split("\u0000").join("")
    .trim()
    .slice(0, maxLength);
};

export const sanitizeTextInput = (value: string | number | null | undefined, maxLength = 1000): string => {
  if (!value) return "";
  const asString = String(value).split("\u0000").join("");

  return escapeHtml(asString)
    .replace(/<[^>]*>/g, "")
    .replace(/javascript:/gi, "")
    .trim()
    .slice(0, maxLength);
};

const ALLOWED_TAGS = new Set([
  "html",
  "head",
  "body",
  "meta",
  "title",
  "p",
  "br",
  "strong",
  "em",
  "u",
  "b",
  "i",
  "ul",
  "ol",
  "li",
  "a",
  "span",
  "div",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "table",
  "thead",
  "tbody",
  "tr",
  "th",
  "td",
]);

const STYLE_CAPABLE_TAGS = new Set([...ALLOWED_TAGS, "style"]);

const ALLOWED_ATTRIBUTES = new Set([
  "href",
  "target",
  "rel",
  "charset",
  "name",
  "content",
  "http-equiv",
]);

const normalizeUrl = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return "";
  return /^\s*javascript:/i.test(trimmed) ? "" : trimmed;
};

const sanitizeNode = (node: Element, allowStyleTag = false): string => {
  const tagName = node.tagName.toLowerCase();
  const allowedTags = allowStyleTag ? STYLE_CAPABLE_TAGS : ALLOWED_TAGS;
  if (!allowedTags.has(tagName)) {
    if (tagName === "style") {
      return "";
    }
    return node.textContent ? escapeHtml(node.textContent) : "";
  }

  const attrs = Array.from(node.attributes)
    .map((attribute) => {
      const normalizedName = attribute.name.toLowerCase();
      if (!ALLOWED_ATTRIBUTES.has(normalizedName)) return null;
      if (normalizedName === "href") {
        const safeUrl = normalizeUrl(attribute.value);
        if (!safeUrl) return null;
        return `${normalizedName}="${escapeHtml(safeUrl)}"`;
      }
      return `${normalizedName}="${escapeHtml(attribute.value)}"`;
    })
    .filter((entry): entry is string => Boolean(entry))
    .join(" ");

  const attributes = attrs ? ` ${attrs}` : "";
  const inner = Array.from(node.childNodes)
    .map((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        return escapeHtml(child.textContent ?? "");
      }
      if (child.nodeType === Node.ELEMENT_NODE) {
        return sanitizeNode(child as Element, allowStyleTag);
      }
      return "";
    })
    .join("");

  if (tagName === "br" && inner === "") {
    return `<${tagName}${attributes} />`;
  }

  return `<${tagName}${attributes}>${inner}</${tagName}>`;
};

export const sanitizeRichHtml = (
  value: string | null | undefined,
  maxLength = 8000,
  options?: { allowStyleTag?: boolean },
): string => {
  if (!value) return "";
  const trimmed = String(value).slice(0, maxLength);
  if (typeof DOMParser === "undefined") {
    return sanitizeTextInput(trimmed, maxLength);
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<root>${trimmed}</root>`, "text/html");
  const root = doc.body.querySelector("root");

  if (!root) return "";
  return Array.from(root.childNodes)
    .map((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        return escapeHtml(child.textContent ?? "");
      }
      if (child.nodeType === Node.ELEMENT_NODE) {
        return sanitizeNode(child as Element, Boolean(options?.allowStyleTag));
      }
      return "";
    })
    .join("");
};

export const sanitizeRichHtmlInput = (value: string | null | undefined) => sanitizeRichHtml(value, 16_000);
export const sanitizeHtmlForRender = (value: string | null | undefined, maxLength = 8000): string => sanitizeRichHtml(value, maxLength);
export const sanitizeForRender = sanitizeHtmlForRender;
export const sanitizeForText = (value: string | null | undefined, maxLength = 1000): string =>
  sanitizeTextInput(value, maxLength);
export const sanitizeTextNode = (value: string | null | undefined, maxLength = 1000): string =>
  sanitizeTextInput(value, maxLength);

export const sanitizeForSrcDoc = (value: string | null | undefined, maxLength = 12000): string =>
  sanitizeRichHtml(value, maxLength, { allowStyleTag: true });

export const sanitizeAttributeValue = (value: string | null | undefined, maxLength = 2000): string => {
  const safe = sanitizeTextInput(value, maxLength);
  return safe;
};

export const sanitizeForPlainText = (value: string | null | undefined, maxLength = 1000): string =>
  sanitizeTextInput(value, maxLength)
    .replace(/\r\n/g, "\n");

export function sanitizeSvgContent(svg: string | null | undefined): string | null {
  if (!svg) return null;
  const trimmed = svg.trim();
  if (!trimmed) return null;

  const parser = new DOMParser();
  const doc = parser.parseFromString(trimmed, "image/svg+xml");

  if (doc.querySelector("parsererror")) {
    return null;
  }

  const root = doc.documentElement;
  if (!root || root.tagName.toLowerCase() !== "svg") {
    return null;
  }

  doc.querySelectorAll("script").forEach((script) => script.remove());

  doc.querySelectorAll("*").forEach((node) => {
    Array.from(node.attributes).forEach((attribute) => {
      const name = attribute.name.toLowerCase();
      const value = attribute.value.trim().toLowerCase();
      if (name.startsWith("on")) {
        node.removeAttribute(attribute.name);
      }
      if ((name === "href" || name === "xlink:href") && value.startsWith("javascript:")) {
        node.removeAttribute(attribute.name);
      }
    });
  });

  const serializer = new XMLSerializer();
  return serializer.serializeToString(root);
}
